/* ============================================
   Suscripción — nivel cuenta (/portal/suscripcion)
   El plan es de la EMPRESA, no del contacto: da una cuota mensual de créditos
   que se consume con todas sus apps.

   La tarjeta NUNCA pasa por acá: el botón manda al checkout de Mercado Pago
   (init_point). Nosotros solo guardamos el preapproval_id.
   ============================================ */

import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { formatCurrency } from '@/lib/utils'
import { Breadcrumbs, Badge, Button } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'

interface Plan {
  id: number
  slug: string
  nombre: string
  descripcion: string | null
  precio: string
  moneda: string
  frecuencia: string
  creditos_incluidos: number
}

interface MedioPago { marca?: string; ultimos4?: string | null }

interface Suscripcion {
  id: number
  plan_id: number
  estado: 'pendiente' | 'activa' | 'pausada' | 'impaga' | 'cancelada'
  monto: string
  moneda: string
  creditos_incluidos: number
  proximo_cobro: string | null
  medio_pago: MedioPago | null
  ultimo_cobro_at: string | null
}

interface Cobro {
  id: number
  monto: string
  moneda: string
  estado: string
  periodo: string | null
  factura_id: number | null
  creditos_acreditados: number | null
  created_at: string
}

interface Respuesta {
  planes: Plan[]
  suscripcion: Suscripcion | null
  cobros: Cobro[]
  /** Precio del crédito suelto: el ancla contra la que se compara cada plan. */
  precio_credito_adicional: number | null
  moneda_creditos: string
  puede_gestionar: boolean
}

/** Costo por crédito de un plan. Cuantos más créditos, más barato sale cada uno. */
function porCredito(p: Plan): number {
  return Number(p.precio) / p.creditos_incluidos
}

/** Precio por crédito con la precisión justa: S/ 0,0245 dice más que S/ 0,02. */
function fmtCredito(v: number, moneda: string): string {
  const simbolo = moneda === 'PEN' ? 'S/' : '$'
  return `${simbolo} ${v.toFixed(4).replace('.', ',')}`
}

const TONO: Record<Suscripcion['estado'], BadgeVariant> = {
  activa: 'activo',
  pendiente: 'pausado',
  impaga: 'rechazada',
  pausada: 'pausado',
  cancelada: 'cancelado',
}

const ROTULO: Record<Suscripcion['estado'], string> = {
  activa: 'Activa',
  pendiente: 'Falta cargar tarjeta',
  impaga: 'Pago rechazado',
  pausada: 'Pausada',
  cancelada: 'Cancelada',
}

function fecha(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function PortalSuscripcion() {
  const { activeClienteId: clienteId } = usePortalCliente()
  const api = useApi()
  const [params, setParams] = useSearchParams()

  const [data, setData] = useState<Respuesta | null>(null)
  const [loading, setLoading] = useState(true)
  const [enviando, setEnviando] = useState<number | 'tarjeta' | 'cancelar' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [volvioDeMP, setVolvioDeMP] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const r = await api.get<Respuesta>('/api/portal/suscripcion')
      setData(r ?? null)
    } catch {
      setError('No pudimos cargar tu plan. Recargá la página.')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { if (clienteId) cargar() }, [clienteId, cargar])

  // Vuelta desde el checkout de MP: refrescamos, porque el webhook puede tardar.
  useEffect(() => {
    if (params.get('ok') === '1') {
      setVolvioDeMP(true)
      params.delete('ok')
      setParams(params, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function contratar(planId: number) {
    setEnviando(planId); setError(null)
    try {
      const r = await api.post<{ init_point: string }>('/api/portal/suscripcion', { plan_id: planId })
      if (r?.init_point) window.location.href = r.init_point
      else { setError('Mercado Pago no devolvió el link de pago.'); setEnviando(null) }
    } catch (e: any) {
      setError(e?.message || 'No pudimos iniciar la contratación.')
      setEnviando(null)
    }
  }

  async function cambiarTarjeta() {
    setEnviando('tarjeta'); setError(null)
    try {
      const r = await api.post<{ init_point: string }>('/api/portal/suscripcion/cambiar-tarjeta', {})
      if (r?.init_point) window.location.href = r.init_point
      else { setError('Mercado Pago no devolvió el link de pago.'); setEnviando(null) }
    } catch (e: any) {
      setError(e?.message || 'No pudimos abrir el checkout.')
      setEnviando(null)
    }
  }

  async function cambiarPlan(planId: number, nombre: string, sube: boolean) {
    const texto = sube
      ? `¿Cambiar a ${nombre}? El nuevo importe y la cuota mayor de créditos entran en tu próximo cobro. Los créditos de este mes ya están acreditados y no se pierden.`
      : `¿Cambiar a ${nombre}? El nuevo importe entra en tu próximo cobro. Los créditos de este mes ya están acreditados y los conservás.`
    if (!confirm(texto)) return
    setEnviando(planId); setError(null)
    try {
      await api.post('/api/portal/suscripcion/cambiar-plan', { plan_id: planId })
      await cargar()
    } catch (e: any) {
      setError(e?.message || 'No pudimos cambiar el plan.')
    } finally {
      setEnviando(null)
    }
  }

  async function cancelar() {
    if (!confirm('¿Cancelar tu plan? Vas a conservar los créditos que te queden, pero dejaremos de acreditarte la cuota mensual.')) return
    setEnviando('cancelar'); setError(null)
    try {
      await api.post('/api/portal/suscripcion/cancelar', {})
      await cargar()
    } catch (e: any) {
      setError(e?.message || 'No pudimos cancelar el plan.')
    } finally {
      setEnviando(null)
    }
  }

  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        <p>Cargando tu plan...</p>
      </div>
    )
  }

  const sus = data?.suscripcion ?? null
  const puede = data?.puede_gestionar ?? false
  const plan = sus ? data?.planes.find(p => p.id === sus.plan_id) : null
  const suelto = data?.precio_credito_adicional ?? null

  return (
    <div className="pi-layout">
      <div className="pi-head">
        <Breadcrumbs
          items={[{ label: 'Portal', to: '/portal' }, { label: 'Suscripción' }]}
          meta={sus ? ROTULO[sus.estado] : 'Sin plan'}
        />
      </div>

      {volvioDeMP && (
        <div className="sus-aviso sus-aviso--ok">
          Recibimos tu registro en Mercado Pago. La activación puede demorar unos minutos en verse acá.
        </div>
      )}
      {error && <div className="sus-aviso sus-aviso--error">{error}</div>}

      {/* ─── Sin plan: vitrina ─── */}
      {!sus && (
        <>
          <div className="sus-intro">
            <h2 className="sus-intro-titulo">Un plan, créditos para todas tus apps</h2>
            <p className="sus-intro-texto">
              Los créditos son la moneda común de la plataforma: se consumen con RAGLink,
              Gemini, Agenda y cualquier app que actives. El plan te los acredita todos los meses,
              y cuanto más grande el plan, más barato te sale cada crédito.
            </p>
          </div>

          {(data?.planes.length ?? 0) === 0 ? (
            <div className="pt-fact-empty" style={{ padding: '48px 24px' }}>
              <h3>Todavía no hay planes publicados</h3>
              <p>Escribinos y armamos uno a la medida de tu consumo.</p>
            </div>
          ) : (
            <div className="sus-planes">
              {data!.planes.map(p => (
                <div key={p.id} className="sus-plan">
                  <div className="sus-plan-nombre">{p.nombre}</div>
                  <div className="sus-plan-precio">
                    {formatCurrency(Number(p.precio), p.moneda)}
                    <small>/{p.frecuencia === 'anual' ? 'año' : 'mes'}</small>
                  </div>
                  <div className="sus-plan-creditos">
                    {p.creditos_incluidos.toLocaleString('es-PE')} créditos incluidos
                  </div>
                  <div className="sus-plan-unit">
                    {fmtCredito(porCredito(p), p.moneda)} por crédito
                    {suelto != null && porCredito(p) < suelto && (
                      <span className="sus-plan-ahorro">
                        −{Math.round((1 - porCredito(p) / suelto) * 100)}%
                      </span>
                    )}
                  </div>
                  {p.descripcion && <p className="sus-plan-desc">{p.descripcion}</p>}
                  {puede && (
                    <Button
                      variant="primary"
                      onClick={() => contratar(p.id)}
                      disabled={enviando !== null}
                    >
                      {enviando === p.id ? 'Abriendo Mercado Pago…' : 'Contratar'}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {suelto != null && (
            <div className="sus-ref">
              <span className="sus-ref-label">Sin plan</span>
              <span className="sus-ref-val">{fmtCredito(suelto, data?.moneda_creditos ?? 'PEN')} por crédito</span>
              <span className="sus-ref-txt">
                Es el precio de referencia del crédito suelto. Cualquier plan te sale más barato.
              </span>
            </div>
          )}

          {!puede && (
            <p className="sus-nota">
              Solo el contacto principal o el de facturación de tu empresa puede contratar un plan.
            </p>
          )}
        </>
      )}

      {/* ─── Con plan ─── */}
      {sus && (
        <>
          {sus.estado === 'pendiente' && (
            <div className="sus-aviso sus-aviso--warn">
              <strong>Falta cargar tu tarjeta.</strong> Tu plan queda activo apenas la registres
              en Mercado Pago.
              {puede && (
                <Button variant="primary" onClick={cambiarTarjeta} disabled={enviando !== null}>
                  {enviando === 'tarjeta' ? 'Abriendo…' : 'Cargar tarjeta'}
                </Button>
              )}
            </div>
          )}

          {sus.estado === 'impaga' && (
            <div className="sus-aviso sus-aviso--error">
              <strong>No pudimos cobrar tu plan.</strong> Mercado Pago rechazó el último cobro.
              Actualizá tu tarjeta para no perder la cuota mensual de créditos.
              {puede && (
                <Button variant="primary" onClick={cambiarTarjeta} disabled={enviando !== null}>
                  {enviando === 'tarjeta' ? 'Abriendo…' : 'Actualizar tarjeta'}
                </Button>
              )}
            </div>
          )}

          <div className="pi-kpis">
            <div className="pi-kpi">
              <span className="pi-kpi-label">Tu plan</span>
              <span className="pi-kpi-val">{plan?.nombre ?? '—'}</span>
            </div>
            <div className="pi-kpi">
              <span className="pi-kpi-label">Créditos por mes</span>
              <span className="pi-kpi-val">{sus.creditos_incluidos.toLocaleString('es-PE')}</span>
            </div>
            <div className="pi-kpi">
              <span className="pi-kpi-label">Importe</span>
              <span className="pi-kpi-val">{formatCurrency(Number(sus.monto), sus.moneda)}</span>
            </div>
            <div className="pi-kpi">
              <span className="pi-kpi-label">Te sale a</span>
              <span className="pi-kpi-val">
                {fmtCredito(Number(sus.monto) / sus.creditos_incluidos, sus.moneda)}
                <small> por crédito</small>
              </span>
            </div>
            <div className="pi-kpi">
              <span className="pi-kpi-label">Próximo cobro</span>
              <span className="pi-kpi-val">{fecha(sus.proximo_cobro)}</span>
            </div>
          </div>

          {/* Método de pago */}
          <div className="pd-card">
            <div className="pd-card-head">
              <h3>Método de pago</h3>
              <Badge variant={TONO[sus.estado]}>{ROTULO[sus.estado]}</Badge>
            </div>
            <div className="sus-medio">
              <span className="sus-medio-txt">
                {sus.medio_pago?.ultimos4
                  ? `${sus.medio_pago.marca ?? 'Tarjeta'} ···· ${sus.medio_pago.ultimos4}`
                  : 'Sin tarjeta registrada'}
              </span>
              {puede && (
                <div className="sus-medio-acciones">
                  <Button variant="secondary" onClick={cambiarTarjeta} disabled={enviando !== null}>
                    {sus.medio_pago?.ultimos4 ? 'Cambiar tarjeta' : 'Cargar tarjeta'}
                  </Button>
                  {sus.estado !== 'cancelada' && (
                    <Button variant="ghost" onClick={cancelar} disabled={enviando !== null}>
                      {enviando === 'cancelar' ? 'Cancelando…' : 'Cancelar plan'}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <p className="sus-nota">
              Tus datos de tarjeta los guarda Mercado Pago. 1Conecta no los recibe ni los almacena.
            </p>
          </div>

          {/* Cambiar de plan */}
          {puede && (data?.planes.length ?? 0) > 1 && (
            <div className="pd-card">
              <div className="pd-card-head"><h3>Cambiar de plan</h3></div>
              <p className="sus-nota" style={{ marginTop: 0 }}>
                El cambio entra en tu <strong>próximo cobro</strong>. No prorrateamos:
                los créditos de este mes ya están acreditados y son tuyos, subas o bajes.
                Si te quedaste corto ahora, conviene una recarga suelta antes que cambiar de plan.
              </p>
              <div className="sus-cambio">
                {data!.planes.filter(p => p.id !== sus.plan_id).map(p => {
                  const sube = Number(p.precio) > Number(sus.monto)
                  return (
                    <div key={p.id} className="sus-cambio-fila">
                      <div className="sus-cambio-info">
                        <strong>{p.nombre}</strong>
                        <span>
                          {formatCurrency(Number(p.precio), p.moneda)}/mes ·{' '}
                          {p.creditos_incluidos.toLocaleString('es-PE')} créditos ·{' '}
                          {fmtCredito(porCredito(p), p.moneda)} c/u
                        </span>
                      </div>
                      <Button
                        variant={sube ? 'primary' : 'secondary'}
                        onClick={() => cambiarPlan(p.id, p.nombre, sube)}
                        disabled={enviando !== null}
                      >
                        {enviando === p.id ? 'Cambiando…' : sube ? 'Subir a este' : 'Bajar a este'}
                      </Button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Historial */}
          <div className="pd-card">
            <div className="pd-card-head"><h3>Historial de cobros</h3></div>
            {(data?.cobros.length ?? 0) === 0 ? (
              <p className="sus-nota">Todavía no hay cobros registrados.</p>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Fecha</th><th>Período</th><th>Importe</th>
                      <th>Créditos</th><th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data!.cobros.map(cb => (
                      <tr key={cb.id}>
                        <td>{fecha(cb.created_at)}</td>
                        <td>{cb.periodo ? cb.periodo.slice(0, 7) : '—'}</td>
                        <td>{formatCurrency(Number(cb.monto), cb.moneda)}</td>
                        <td>{cb.creditos_acreditados?.toLocaleString('es-PE') ?? 'En proceso'}</td>
                        <td>
                          <Badge variant={cb.estado === 'aprobado' ? 'completado' : cb.estado === 'rechazado' ? 'rechazada' : 'pausado'}>
                            {cb.estado}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
